import { TermsDocumentPublic } from '../../../types';

interface TermsTimelineRailProps {
  documents: TermsDocumentPublic[];
  activeIndex: number;
  agreements: Map<string, boolean>;
  isSummaryView: boolean;
  isArabic: boolean;
  allAgreed: boolean;
  onTimelineClick: (index: number) => void;
  labels: {
    upNext: string;
    agreed: string;
    finalReview: string;
    unlocksAfter: string;
  };
}

export default function TermsTimelineRail({
  documents,
  activeIndex,
  agreements,
  isSummaryView,
  isArabic,
  allAgreed,
  onTimelineClick,
  labels,
}: TermsTimelineRailProps) {
  const totalDocs = documents.length;

  const getDocTitle = (doc: TermsDocumentPublic) =>
    isArabic && doc.titleAr ? doc.titleAr : doc.title;

  return (
    <div className="terms-timeline">
      {documents.map((doc, index) => {
        const isSelected = !isSummaryView && index === activeIndex;
        const isAgreed = agreements.get(doc.id) ?? false;
        const everyPriorAgreed = documents
          .slice(0, index)
          .every((d) => agreements.get(d.id));
        const isUpNext = !isAgreed && !isSelected && everyPriorAgreed;
        const isLocked = !isAgreed && !isSelected && !everyPriorAgreed;

        const cls = [
          'terms-timeline-item',
          isSelected ? 'selected' : '',
          isAgreed ? 'agreed' : '',
          isUpNext ? 'up-next' : '',
          isLocked ? 'locked' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div
            key={doc.id}
            className={cls}
            onClick={() => onTimelineClick(index)}
            role="button"
            tabIndex={isLocked ? -1 : 0}
            aria-disabled={isLocked}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !isLocked) {
                e.preventDefault();
                onTimelineClick(index);
              }
            }}
          >
            <div className="terms-timeline-indicator">
              <div className={`terms-timeline-circle${isAgreed ? ' agreed' : ''}`}>
                {isAgreed ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <div className="terms-timeline-line" />
            </div>
            <div className="terms-timeline-label">
              <span>{getDocTitle(doc)}</span>
              {isUpNext && <em className="terms-timeline-cue">{labels.upNext}</em>}
              {isAgreed && !isSelected && (
                <em className="terms-timeline-cue agreed-cue">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  {labels.agreed}
                </em>
              )}
            </div>
          </div>
        );
      })}

      <div
        className={[
          'terms-timeline-item',
          'final-review',
          isSummaryView ? 'selected' : '',
          allAgreed ? 'up-next' : 'locked',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => allAgreed && onTimelineClick(totalDocs)}
        role="button"
        tabIndex={allAgreed ? 0 : -1}
        aria-disabled={!allAgreed}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && allAgreed) {
            e.preventDefault();
            onTimelineClick(totalDocs);
          }
        }}
      >
        <div className="terms-timeline-indicator">
          <div className="terms-timeline-circle final">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
        </div>
        <div className="terms-timeline-label">
          <span>{labels.finalReview}</span>
          {!allAgreed && (
            <em className="terms-timeline-cue locked-cue">{labels.unlocksAfter}</em>
          )}
        </div>
      </div>
    </div>
  );
}
